import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import HeaderSection from '../content/HeaderSection';
import FooterSection from '../content/FooterSection';
import useGoogleAnalytics from '../hooks/useGoogleAnalytics';
import { usePageLanguage } from '../context/PageLanguageContext';
import SEOHead from '../components/SEOHead';

// The desktop edition's page. It leads with what the club GETS — an evening that feels like an
// event — and not with how this edition relates to the web one. The reassurance that it is still the
// same scoreboard follows immediately below, where it belongs: nobody is sold by "same as before".
//
// Written to be honest about what it is not: same scoreboard, same rules, no support, thirty days to
// find out for yourself.

const translations = {
  de: {
    pageTitle: 'Desktop-Edition – Rink Hockey Scoreboard',
    pageDescription:
      'Mach aus deiner Halle ein grosses Erlebnis: Video, Musik und Tor-Animationen laufen automatisch zum Spiel. Teamlogos, Kader und Torjubel pro Spielerin. 30 Tage testen.',
    backToHome: 'Zurück zur Startseite',
    tag: 'Desktop-Edition',
    title: 'Mach aus deiner Halle ein grosses Erlebnis',
    lead:
      'Mit Video, Musik und Tor-Animationen — und ohne dass am Pult jemand mitdenken muss. Die Desktop-Edition kann alles, was die Web-Version kann; die Unterhaltung kommt dazu und läuft zum Spiel statt auf Zuruf.',
    ctaPrimary: '30 Tage kostenlos testen',
    ctaSecondary: 'Preise ansehen',
    baseTitle: 'Die Grundlage bleibt dieselbe',
    baseText:
      'Spielzeit, Strafen mit automatischer Teamstrafen-Berechnung, Teamfouls, Timeouts, Pausen, Vereinsfarbe, Sponsorenlogos, zweiter Bildschirm. Wer die Web-Version kennt, findet sich sofort zurecht — es kommt nichts weg, es kommt etwas dazu.',
    plusTag: 'Was dazukommt',
    plusTitle: 'Vier Dinge, die den Unterschied machen',
    features: [
      {
        n: '01',
        title: 'Teamlogos',
        text: 'Hinterlege deine Vereine einmal mit Logo und Kader. Beim Spiel wählst du Heim und Gast aus der Liste — Wappen und Spielerinnen sind da, ohne Tipparbeit am Spieltag.',
      },
      {
        n: '02',
        title: 'Videos nahtlos eingebunden',
        text: 'Pre-Game-Sequenzen, Sponsorenclips, Einlaufvideos: zusammengestellt im Voraus, gestartet mit einem Klick. Das Video übernimmt den Zuschauerbildschirm und gibt ihn danach von selbst wieder frei — die Anzeigetafel ist zurück, bevor du daran denkst.',
      },
      {
        n: '03',
        title: 'Musik vollautomatisch',
        text: 'Ein Knopf über dem Menü, ein Druck, und die Musik läuft — der Song wird selbst gewählt, nie zweimal derselbe hintereinander. Wer will, schaltet die Automatik ein: dann startet die Musik bei einer Unterbrechung und hört auf, wenn die Uhr wieder läuft.',
      },
      {
        n: '04',
        title: 'Torjubel — für den Verein und pro Spielerin',
        text: 'Ein Torjubel für den ganzen Verein, und wo du willst ein eigener Clip pro Spielerin. Ohne eigenes Video zeichnet die App selbst einen Jubel mit deinem Wappen, wahlweise mit Nummer und Name der Torschützin. Gefeiert wird, wenn du es sagst: Beim Unterbruch fragt die App, wer getroffen hat — oder du löst mit einem Knopf einen allgemeinen Jubel aus. Gewinnt ihr zu null, feiert sie beim Schlusspfiff eure Torhüterin.',
      },
    ],
    calmTag: 'Der eigentliche Punkt',
    calmTitle: 'Weniger Handgriffe, weniger schiefgehen',
    calmText:
      'Am Spieltag steht selten der Profi am Pult, sondern wer gerade Zeit hat. Genau dafür ist diese Edition gebaut: was automatisch laufen kann, läuft automatisch. Die bedienende Person kann sich auf Zeit, Tore und Strafen konzentrieren — den Rest macht die App.',
    factsTitle: 'Gut zu wissen',
    facts: [
      'Läuft auf Windows und macOS, installiert oder direkt ab Datei.',
      'Arbeitet offline — in der Halle brauchst du kein Netz.',
      'Konfiguration exportieren und auf dem zweiten Rechner einlesen.',
      '30 Tage voll funktionsfähig testen, ohne Konto.',
      'Software wie sie ist, ohne Supportleistung.',
    ],
    closingTitle: 'Am besten selbst anschauen',
    closingText: 'Dreissig Tage, alle Funktionen, keine Zahlungsdaten. Wenn es nicht passt, läuft es einfach aus.',
  },
  en: {
    pageTitle: 'Desktop edition – Rink Hockey Scoreboard',
    pageDescription:
      'Turn your venue into an event: video, music and goal animations run with the match by themselves. Team logos, rosters and per-player celebrations. 30-day trial.',
    backToHome: 'Back to home',
    tag: 'Desktop edition',
    title: 'Turn your venue into an event',
    lead:
      'With video, music and goal animations — and nobody at the desk having to think about it. The desktop edition does everything the web edition does; the entertainment comes on top and follows the match instead of waiting to be told.',
    ctaPrimary: 'Try it free for 30 days',
    ctaSecondary: 'See pricing',
    baseTitle: 'The foundation is unchanged',
    baseText:
      'Game clock, penalties with automatic team-penalty time, team fouls, timeouts, breaks, club colour, sponsor logos, second screen. If you know the web edition you will be at home immediately — nothing is taken away, something is added.',
    plusTag: 'What comes on top',
    plusTitle: 'Four things that make the difference',
    features: [
      {
        n: '01',
        title: 'Team logos',
        text: 'Set your clubs up once, with crest and roster. On match day you pick home and away from a list — the crest and the players are simply there, with nothing to type.',
      },
      {
        n: '02',
        title: 'Video woven in',
        text: 'Pre-game sequences, sponsor clips, entrance videos: arranged in advance, started with one click. The video takes the audience screen and hands it back by itself — the board is up again before you think about it.',
      },
      {
        n: '03',
        title: 'Music, fully automatic',
        text: 'One button above the menu, one press, and the music plays — the track is picked for you, never the same one twice in a row. Switch the automation on and it starts on a stoppage and stops when the clock runs again.',
      },
      {
        n: '04',
        title: 'Goal celebrations — club-wide and per player',
        text: 'One celebration for the whole club, and a clip of their own for the players you choose. Without a video of your own, the app draws a celebration with your crest, optionally revealing the scorer’s number and name. It celebrates when you say so: at the next stoppage the app asks who scored — or one button gives the hall a general celebration. Win without conceding, and at the final whistle it celebrates your goalkeeper.',
      },
    ],
    calmTag: 'The real point',
    calmTitle: 'Fewer moves, less to go wrong',
    calmText:
      'On match day the desk is rarely staffed by a specialist, but by whoever has the time. That is what this edition is built for: whatever can run by itself does. The operator can watch the clock, the goals and the penalties — the app takes the rest.',
    factsTitle: 'Worth knowing',
    facts: [
      'Runs on Windows and macOS, installed or straight from the file.',
      'Works offline — no network needed in the hall.',
      'Export the configuration and load it on the second machine.',
      'Thirty days, fully functional, no account.',
      'Software as it is, with no support.',
    ],
    closingTitle: 'Best seen for yourself',
    closingText: 'Thirty days, every feature, no payment details. If it does not suit you, it simply runs out.',
  },
};

export default function Desktop() {
  useGoogleAnalytics();
  const { pageLanguage } = usePageLanguage();
  const t = translations[pageLanguage] || translations.de;
  const navigate = useNavigate();

  return (
    <div className="min-h-screen flex flex-col">
      <SEOHead
        title={t.pageTitle}
        description={t.pageDescription}
        lang={pageLanguage}
        canonical="https://www.rinkhockey-scoreboard.com/desktop"
      />
      <HeaderSection />

      <main className="flex-grow">
        {/* Hero */}
        <section className="w-full bg-gradient-to-b from-slate-900 to-slate-800 text-white">
          <div className="mx-auto max-w-[1200px] px-8 py-24 max-md:px-5 max-md:py-16">
            <Link to="/" className="text-sm text-slate-300 hover:text-white">
              ← {t.backToHome}
            </Link>
            <div className="mt-8 max-w-[860px]">
              <div className="text-sm font-semibold uppercase tracking-wider text-blue-400">{t.tag}</div>
              <h1 className="mt-3 text-5xl font-extrabold leading-tight max-md:text-3xl">{t.title}</h1>
              <p className="mt-6 text-lg leading-relaxed text-slate-300">{t.lead}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <button
                  type="button"
                  onClick={() => navigate('/preise')}
                  className="rounded-lg bg-blue-600 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
                >
                  {t.ctaPrimary}
                </button>
                <button
                  type="button"
                  onClick={() => navigate('/preise')}
                  className="rounded-lg border border-slate-500 px-6 py-4 text-sm font-semibold text-white transition-colors hover:bg-slate-700"
                >
                  {t.ctaSecondary}
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* The base is unchanged — said first, because it is the reassurance a club needs. */}
        <section className="w-full bg-white">
          <div className="mx-auto max-w-[1200px] px-8 py-16 max-md:px-5">
            <div className="rounded-2xl border border-stone-200 bg-stone-50 p-8 md:p-10">
              <h2 className="text-2xl font-bold text-stone-900">{t.baseTitle}</h2>
              <p className="mt-3 max-w-4xl text-lg leading-relaxed text-stone-600">{t.baseText}</p>
            </div>
          </div>
        </section>

        {/* The four additions */}
        <section className="w-full bg-white">
          <div className="mx-auto max-w-[1200px] px-8 pb-8 max-md:px-5">
            <div className="mb-12 max-w-[800px]">
              <div className="text-sm font-semibold uppercase tracking-wider text-blue-600">{t.plusTag}</div>
              <h2 className="mt-3 text-4xl font-extrabold leading-tight text-stone-900 max-md:text-3xl">
                {t.plusTitle}
              </h2>
            </div>
            <div className="grid gap-6 md:grid-cols-2">
              {t.features.map((f) => (
                <div key={f.n} className="rounded-2xl border border-stone-200 p-8 transition-shadow hover:shadow-lg">
                  <div className="text-sm font-bold text-blue-600">{f.n}</div>
                  <h3 className="mt-3 text-2xl font-bold text-stone-900">{f.title}</h3>
                  <p className="mt-3 text-base leading-relaxed text-stone-600">{f.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Why it matters */}
        <section className="w-full bg-white">
          <div className="mx-auto max-w-[1200px] px-8 py-20 max-md:px-5 max-md:py-14">
            <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-10 text-white md:p-14">
              <div className="text-sm font-semibold uppercase tracking-wider text-blue-400">{t.calmTag}</div>
              <h2 className="mt-3 max-w-3xl text-3xl font-bold leading-tight md:text-4xl">{t.calmTitle}</h2>
              <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-300">{t.calmText}</p>
            </div>
          </div>
        </section>

        {/* Plain facts, including the ones a buyer should know before paying. */}
        <section className="w-full bg-gray-50">
          <div className="mx-auto max-w-[1200px] px-8 py-16 max-md:px-5">
            <h2 className="text-2xl font-bold text-stone-900">{t.factsTitle}</h2>
            <ul className="mt-6 grid gap-3 md:grid-cols-2">
              {t.facts.map((f) => (
                <li key={f} className="flex gap-3 text-base text-stone-700">
                  <span className="mt-1 text-blue-600" aria-hidden="true">•</span>
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Close */}
        <section className="w-full bg-white">
          <div className="mx-auto max-w-[1200px] px-8 py-20 text-center max-md:px-5 max-md:py-14">
            <h2 className="text-3xl font-extrabold text-stone-900">{t.closingTitle}</h2>
            <p className="mx-auto mt-4 max-w-2xl text-lg text-stone-600">{t.closingText}</p>
            <button
              type="button"
              onClick={() => navigate('/preise')}
              className="mt-8 rounded-lg bg-blue-600 px-7 py-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700"
            >
              {t.ctaPrimary}
            </button>
          </div>
        </section>
      </main>

      <FooterSection lang={pageLanguage} />
    </div>
  );
}
